import * as Sentry from '@sentry/react';
import {useEffect} from 'react';
import {isRouteErrorResponse, useRouteError} from 'react-router';

/**
 * Root `errorElement` for the data router.
 *
 * React Router's data router catches render/loader errors in its own error
 * boundary. Without an `errorElement` it renders a bare default page and the
 * error never reaches `window.onerror`, so Sentry's global handler (and the
 * `wrapCreateBrowserRouterV7` tracing integration, which does not capture
 * boundary errors) never sees it — that is why the FacebookSignin crash on
 * /auth/login produced no Sentry issue.
 *
 * This boundary forwards genuine render errors to Sentry and shows a minimal,
 * provider-independent fallback (plain DOM + inline styles, no Intl/MUI theme
 * context) so it can render even when the app shell itself failed.
 */
const RouteErrorBoundary = () => {
  const error = useRouteError();

  useEffect(() => {
    // Intentional route responses (404s, redirects, thrown Responses) are not
    // application crashes — don't report those as Sentry exceptions.
    if (!isRouteErrorResponse(error)) {
      Sentry.captureException(error);
    }
  }, [error]);

  return (
    <div
      role="alert"
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: 24,
        textAlign: 'center',
        fontFamily:
          'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      }}>
      <h1 style={{margin: 0, fontSize: 24}}>Something went wrong</h1>
      <p style={{margin: 0, maxWidth: 420, color: '#666'}}>
        An unexpected error occurred. Our team has been notified. Please reload
        the page or try again in a moment.
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          padding: '10px 20px',
          fontSize: 16,
          borderRadius: 8,
          border: 'none',
          backgroundColor: '#1976d2',
          color: '#fff',
          cursor: 'pointer',
        }}>
        Reload
      </button>
    </div>
  );
};

export default RouteErrorBoundary;
